import React, {useState} from 'react'
import moment from 'moment'
import {Link} from 'react-router-dom'
import {Badge, Button, Table} from 'react-bootstrap'
import {defineMessages, FormattedMessage, useIntl} from 'react-intl'
import {ContactPolicyGroup} from './contactProfileTypes'
import {parseId} from '../../../../helpers'
import {getDocumentVariant, getPaymentVariant, getBadgeTextColor} from '../../../../helpers/consts'
import {UserAvatar} from '../../../../components/UserAvatar'
import {AgentInfoTooltip} from '../../../shared/AgentInfoTooltip'
import {BadgeStatePolicy} from '../../../onePolicy/components/BadgeStatePolicy'
import {getColorByStatus, getTitleByStatus} from '../../../shared/utils'
import {paymentStatusLabel} from '../../../../helpers/catalogMessages'
import {AllPolicyStatusesData} from '../../../../types/policies'
import {PolicySummary} from '../../../../types/contactsApi'

const sp = ' '

/**
 * La pestana sirve tanto a cotizaciones como a polizas. En vez de pegar el
 * sustantivo dentro de la frase, se define una frase completa por tipo: asi el
 * ES puede reordenar y concordar en genero.
 */
const QUOTE_MESSAGES = defineMessages({
  title: {id: 'contacts.profile.section.quotes', defaultMessage: 'Quotes'},
  count: {
    id: 'contacts.profile.records.quoteCount',
    defaultMessage: '{count, plural, one {# quote} other {# quotes}}',
  },
  empty: {
    id: 'contacts.profile.records.noQuotes',
    defaultMessage: 'No quotes found for this contact.',
  },
  noneActive: {
    id: 'contacts.profile.records.noActiveQuotes',
    defaultMessage: 'No active quotes. Use the toggle above to view archived records.',
  },
  archivedHeading: {
    id: 'contacts.profile.records.archivedQuotes',
    defaultMessage: 'Archived quotes',
  },
  showArchived: {
    id: 'contacts.profile.records.showArchivedQuotes',
    defaultMessage: 'Show archived quotes ({count})',
  },
  hideArchived: {
    id: 'contacts.profile.records.hideArchivedQuotes',
    defaultMessage: 'Hide archived quotes ({count})',
  },
})

const POLICY_MESSAGES = defineMessages({
  title: {id: 'contacts.profile.section.policies', defaultMessage: 'Policies'},
  count: {
    id: 'contacts.profile.records.policyCount',
    defaultMessage: '{count, plural, one {# policy} other {# policies}}',
  },
  empty: {
    id: 'contacts.profile.records.noPolicies',
    defaultMessage: 'No policies found for this contact.',
  },
  noneActive: {
    id: 'contacts.profile.records.noActivePolicies',
    defaultMessage: 'No active policies. Use the toggle above to view archived records.',
  },
  archivedHeading: {
    id: 'contacts.profile.records.archivedPolicies',
    defaultMessage: 'Archived policies',
  },
  showArchived: {
    id: 'contacts.profile.records.showArchivedPolicies',
    defaultMessage: 'Show archived policies ({count})',
  },
  hideArchived: {
    id: 'contacts.profile.records.hideArchivedPolicies',
    defaultMessage: 'Hide archived policies ({count})',
  },
})

const SHARED_MESSAGES = defineMessages({
  archivedBadge: {id: 'contacts.profile.records.archivedBadge', defaultMessage: 'Archived'},
})

const COLUMN_COUNT = 5

/** `UserAvatar` / `AgentInfoTooltip` esperan strings; la API los devuelve nullables. */
const agentUser = (user: PolicySummary['user']) =>
  user
    ? {
        ...user,
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
      }
    : null

interface ContactPoliciesTabProps {
  groups: ContactPolicyGroup[]
  recordType: 'POLICY' | 'QUOTE'
  policiesStatuses: AllPolicyStatusesData | undefined
  onNavigate?: () => void
}

export const ContactPoliciesTab: React.FC<ContactPoliciesTabProps> = ({
  groups,
  recordType,
  policiesStatuses,
  onNavigate,
}) => {
  const intl = useIntl()
  const na = intl.formatMessage({id: 'common.value.notAvailable', defaultMessage: 'N/A'})
  const statusEdges = policiesStatuses?.allPolicyStatuses?.edges ?? []
  const [showArchived, setShowArchived] = useState(false)
  const isArchived = ({policy}: ContactPolicyGroup['entries'][number]) =>
    Boolean(policy.archivedDate || policy.isArchived)
  const activeGroups = groups
    .map((group) => ({...group, entries: group.entries.filter((entry) => !isArchived(entry))}))
    .filter((group) => group.entries.length > 0)
  const archivedGroups = groups
    .map((group) => ({...group, entries: group.entries.filter(isArchived)}))
    .filter((group) => group.entries.length > 0)
  const displayGroups = [
    ...activeGroups.map((group) => ({...group, section: 'active' as const})),
    ...(showArchived
      ? archivedGroups.map((group) => ({...group, section: 'archived' as const}))
      : []),
  ]
  const total = activeGroups.reduce((acc, group) => acc + group.entries.length, 0)
  const archivedTotal = archivedGroups.reduce((acc, group) => acc + group.entries.length, 0)
  const isQuote = recordType === 'QUOTE'
  const M = isQuote ? QUOTE_MESSAGES : POLICY_MESSAGES

  return (
    <div className='policy-form-tabs-container'>
      <div className='kt-heading kt-heading--md custom-border-brand d-flex align-items-center justify-content-between'>
        <span>
          <FormattedMessage {...M.title} />
        </span>
        <div className='d-flex align-items-center gap-3'>
          {archivedTotal > 0 && (
            <Button
              variant='link'
              size='sm'
              className='d-flex align-items-center gap-1 p-0 text-muted text-decoration-none fw-normal'
              style={{fontSize: '0.85rem', lineHeight: 1.2}}
              aria-pressed={showArchived}
              onClick={() => setShowArchived((current) => !current)}
            >
              <i className={`bi bi-${showArchived ? 'eye-slash' : 'eye'}`} />
              <FormattedMessage
                {...(showArchived ? M.hideArchived : M.showArchived)}
                values={{count: archivedTotal}}
              />
            </Button>
          )}
          <Badge bg='secondary' text='dark'>
            <FormattedMessage {...M.count} values={{count: total}} />
          </Badge>
        </div>
      </div>

      {groups.length === 0 ? (
        <div className='text-muted py-4 text-center'>
          <FormattedMessage {...M.empty} />
        </div>
      ) : displayGroups.length === 0 ? (
        <div className='text-muted py-4 text-center'>
          <FormattedMessage {...M.noneActive} />
        </div>
      ) : (
        // Una sola tabla para todos los grupos: cabecera única y el tipo de póliza como
        // fila separadora, para que las columnas queden alineadas entre grupos.
        <Table responsive size='sm' className='mb-0 align-middle'>
          <thead>
            <tr>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.agentOwner'
                  defaultMessage='Agent Owner'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.policy'
                  defaultMessage='Policy'
                />
              </th>
              <th>
                <FormattedMessage
                  id='common.field.status'
                  defaultMessage='Status'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.agentOfRecord'
                  defaultMessage='Agent of Record'
                />
              </th>
              <th>
                <FormattedMessage
                  id='contacts.profile.policies.col.role'
                  defaultMessage='Role/Relationship'
                />
              </th>
            </tr>
          </thead>
          {displayGroups.map((group, groupIndex) => (
            <tbody key={`${group.section}-${group.type}`}>
              {group.section === 'archived' &&
                (groupIndex === 0 || displayGroups[groupIndex - 1].section !== 'archived') && (
                  <tr>
                    <td colSpan={COLUMN_COUNT} className='bg-secondary-subtle fw-bold'>
                      <FormattedMessage {...M.archivedHeading} />
                    </td>
                  </tr>
                )}
              <tr>
                <td colSpan={COLUMN_COUNT} className='bg-light'>
                  <div className='d-flex align-items-center gap-2'>
                    <Badge bg='primary' className='text-white fs-7'>
                      {group.type}
                    </Badge>
                    <span className='text-muted' style={{fontSize: '0.9rem'}}>
                      <FormattedMessage
                        id='contacts.profile.policies.groupCount'
                        defaultMessage='{count} records'
                        values={{count: group.entries.length}}
                      />
                      {group.section === 'archived' && (
                        <Badge bg='secondary' className='ms-2'>
                          <FormattedMessage {...SHARED_MESSAGES.archivedBadge} />
                        </Badge>
                      )}
                    </span>
                  </div>
                </td>
              </tr>
              {group.entries.map(({policy, related}) => {
                const applicantsCount =
                  policy.applicants && policy.applicants > 0
                    ? policy.applicants
                    : policy.members ?? 0
                return (
                  <tr key={policy.id}>
                    <td>
                      <UserAvatar
                        user={agentUser(policy.user)}
                        tooltipId={`contact-policy-agent-${policy.id}`}
                        size={40}
                      />
                      <AgentInfoTooltip
                        id={`contact-policy-agent-${policy.id}`}
                        user={agentUser(policy.user)}
                      />
                    </td>
                    <td className='limit-width'>
                      <div className='info-policy-name'>
                        <Link
                          className='text-primary text-link fw-bold'
                          to={`/${recordType === 'QUOTE' ? 'one-quote' : 'one-policy'}/${parseId(
                            policy.id
                          )}`}
                          onClick={onNavigate}
                        >
                          {policy.carrier?.name || policy.policyName || na}
                        </Link>
                        <p className='m-0 p-0' style={{fontSize: '0.95rem'}}>
                          <FormattedMessage
                            id='contacts.profile.policies.effective'
                            defaultMessage='Effective:'
                          />
                          {sp}
                          {policy.effectiveDate
                            ? moment(policy.effectiveDate).format('MMM DD, YYYY')
                            : na}
                        </p>
                        <div className='d-flex align-items-center gap-2 mt-1 flex-wrap'>
                          <Badge
                            bg='primary'
                            className='d-flex align-items-center gap-1 text-white'
                            title={intl.formatMessage(
                              {
                                id: 'contacts.profile.policies.applicants',
                                defaultMessage:
                                  '{count, plural, one {# Applicant} other {# Applicants}}',
                              },
                              {count: applicantsCount}
                            )}
                          >
                            <i className='bi bi-people-fill' />
                            {applicantsCount}
                          </Badge>
                          {policy.memberId && (
                            <span className='m-0 p-0' style={{fontSize: '0.9rem'}}>
                              <FormattedMessage
                                id='contacts.profile.policies.memberId'
                                defaultMessage='ID:'
                              />
                              {sp}
                              {policy.memberId}
                            </span>
                          )}
                          {policy.hasAutoPolicy === true && (
                            <Badge
                              bg='danger'
                              className='d-flex align-items-center text-white'
                              title={intl.formatMessage({
                                id: 'contacts.profile.policies.hasAuto',
                                defaultMessage: 'Has auto policy',
                              })}
                            >
                              <i
                                className='bi bi-car-front-fill'
                                style={{color: 'var(--bs-white)'}}
                              />
                            </Badge>
                          )}
                          {policy.hasHomePolicy === true && (
                            <Badge
                              bg='success'
                              className='d-flex align-items-center text-white'
                              title={intl.formatMessage({
                                id: 'contacts.profile.policies.hasHome',
                                defaultMessage: 'Has home policy',
                              })}
                            >
                              <i className='bi bi-house-fill' style={{color: 'var(--bs-white)'}} />
                            </Badge>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      {policy.status ? (
                        <div className='mb-1'>
                          <BadgeStatePolicy
                            status={getTitleByStatus(statusEdges, policy.status)}
                            color={getColorByStatus(statusEdges, policy.status)}
                          />
                        </div>
                      ) : (
                        <div className='text-muted mb-1'>{na}</div>
                      )}
                      {policy.stateDocs ? (
                        <div className='mb-1'>
                          <span className='text-muted me-1'>
                            <FormattedMessage
                              id='contacts.profile.policies.documents'
                              defaultMessage='Documents:'
                            />
                          </span>
                          <Badge className='badge-sm' bg={getDocumentVariant(policy.stateDocs)} text={getBadgeTextColor(getDocumentVariant(policy.stateDocs))}>
                            {policy.stateDocs}
                          </Badge>
                        </div>
                      ) : null}
                      {policy.paymentStatus ? (
                        <div>
                          <span className='text-muted me-1'>
                            <FormattedMessage
                              id='contacts.profile.policies.payment'
                              defaultMessage='Payment:'
                            />
                          </span>
                          <Badge
                            className='badge-sm'
                            bg={getPaymentVariant(policy.paymentStatus)}
                            text={
                              getPaymentVariant(policy.paymentStatus) === 'danger'
                                ? 'white'
                                : undefined
                            }
                          >
                            {paymentStatusLabel(intl, policy.paymentStatus)}
                          </Badge>
                        </div>
                      ) : null}
                    </td>
                    <td>{policy.agentOfRecord || na}</td>
                    <td>
                      <div className='fw-bold'>{related?.rol || na}</div>
                      {related?.rol && related.rol !== 'HOLDER' ? (
                        <Badge bg='dark' text='white' className='badge-sm'>
                          {related.relationship || na}
                        </Badge>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          ))}
        </Table>
      )}
    </div>
  )
}
